import {
  Box,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { Candidate, Precinct, Voter } from '../../types';
import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PrecinctVoterDetails from './PrecinctVoterDetails';
import { db } from '../../firebase';

interface PrecinctVotesProps {
  precinct: Precinct;
  position: string;
  candidates: Candidate[];
}

const PrecinctVotes: React.FC<PrecinctVotesProps> = ({ precinct, position, candidates }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [voters, setVoters] = useState<Voter[]>([]);
  const [isTableExpanded, setIsTableExpanded] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [allCandidates, setAllCandidates] = useState<Candidate[]>([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);

  useEffect(() => {
    fetchAllCandidates();
  }, []);

  const fetchAllCandidates = async () => {
    setIsLoadingCandidates(true);
    try {
      const candidatesRef = collection(db, "candidates");
      const querySnapshot = await getDocs(candidatesRef);
      const candidatesList = querySnapshot.docs.map(doc => doc.data() as Candidate);
      setAllCandidates(candidatesList);
    } catch (error) {
      console.error("Error fetching all candidates:", error);
    } finally {
      setIsLoadingCandidates(false);
    }
  };

  const toggleTableExpansion = () => {
    if (!isTableExpanded && voters.length === 0) {
      // Only fetch voters when expanding
      fetchPrecinctVoters();
    }
    setIsTableExpanded(!isTableExpanded);
  };

  const handleRowExpand = (voterId: string | null) => {
    setExpandedRowId(voterId);
  };

  const fetchPrecinctVoters = async () => {
    setIsLoading(true);
    try {
      const votersRef = collection(db, "voters");
      const q = query(votersRef,
        where("province", "==", precinct.province),
        where("city_municipality", "==", precinct.city_municipality),
        where("barangay", "==", precinct.barangay),
        where("precinct", "==", precinct.precinct)
      );
      const querySnapshot = await getDocs(q);
      const _voters = querySnapshot.docs.map(doc => doc.data() as Voter);
      _voters.sort((a, b) => a.name.localeCompare(b.name));
      setVoters(_voters);
    } catch (error) {
      console.error("Error fetching precinct voters:", error);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Box>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          backgroundColor: '#f0f4f8', // Soft blue-gray background
          padding: 1,
          borderRadius: 1 
        }}
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {`Precinct ${precinct.precinct}`}
        </Typography>
        <IconButton onClick={toggleTableExpansion} size="small">
          {isTableExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      {isLoading || isLoadingCandidates ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" width="100%" height={200} />
          </Grid>
          {[1, 2, 3].map((item) => (
            <Grid item xs={12} key={item}>
              <Skeleton variant="rectangular" width="100%" height={150} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Collapse in={isTableExpanded}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Leader</TableCell>
                  <TableCell>Organizations</TableCell>
                  <TableCell>Party Preference</TableCell>
                  <TableCell>Voted Candidate</TableCell>
                  <TableCell>Last Polled By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {voters.map((voter, index) => (
                  <PrecinctVoterDetails
                    key={voter.id}
                    precinct={precinct}
                    position={position}
                    candidates={allCandidates.length > 0 ? allCandidates : candidates}
                    voter={voter}
                    index={index}
                    expandedRowId={expandedRowId}
                    onRowExpand={handleRowExpand}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      )}
    </Box>
  );
};

export default PrecinctVotes;
